import clientFirebase from 'firebase/compat/app';
import 'firebase/compat/database';
import 'firebase/compat/firestore';

/** @import { ClientOrAdminFirebase } from './types/ClientOrAdminFirebase.js' */

/** Roles that should be replicated in Firestore. */
const ROLES_TO_REPLICATE_IN_FIRESTORE = ['timesheet_manager'];

/**
 * @param {string} [uid]
 * @param {string} [companyId]
 * @throws {error} Throws an error if neither uid nor companyId are provided.
 * @throws {error} Throws an error if both uid and companyId are provided.
 */
function EnsureUidOrCompanyIdExistButNotBoth(uid, companyId) {
  if (!uid && !companyId) throw new Error('uid or companyId are required');
  if (uid && companyId) throw new Error('uid and companyId cannot be provided at the same time');

  // TODO: Remove this when companyId is supported.
  if (companyId) throw Error('companyId is not supported yet');
}

/**
 * @param {string} role
 * @throws {error} Throws an error if role is not provided.
 */
function EnsureRoleExists(role) {
  if (!role) throw new Error('role is required');
}

/**
 * @param {string} issuerCompanyId
 * @throws {error} Throws an error if issuerCompanyId is not provided.
 */
function EnsureIssuerCompanyIdExists(issuerCompanyId) {
  if (!issuerCompanyId) throw new Error('issuerCompanyId is required');
}

/**
 * @param {ClientOrAdminFirebase} firebase
 * @throws {error} Throws an error if firebase is not provided.
 */
function EnsureFirebaseExists(firebase) {
  if (!firebase) throw new Error('Firebase was not provided and could not be found globally');
}

/**
 * @param {function} callback
 * @throws {error} callback must exist.
 * @throws {error} callback must be a function.
 */
function EnsureCallbackExistsAndIsAFunction(callback) {
  if (!callback) throw new Error('callback is required');
  if (typeof callback !== 'function') throw new Error('callback must be a function');
}

/**
 * Checks if a user or company has been granted a specific role by another company.
 * @param {object} options
 * @param {string} [options.uid] The uid of the user who has received the role.
 * @param {string} [options.companyId] The id of the company who has received the role.
 * @param {string} options.role The role to check.
 * @param {string} [options.issuerCompanyId] The id of the company who has granted the role.
 * @param {ClientOrAdminFirebase} [options.firebase]
 * @returns {Promise<Boolean>}
 */
async function HasRole(options) {
  const { uid, companyId, role, issuerCompanyId, firebase = clientFirebase } = options ?? {};

  EnsureUidOrCompanyIdExistButNotBoth(uid, companyId);
  EnsureRoleExists(role);
  EnsureFirebaseExists(firebase);

  const userOrCompanySubpath = uid ? `users/${uid}` : `companies/${companyId}`;

  const globalRoleRef = firebase.database().ref(`roles/issuer_companies/__all__/${userOrCompanySubpath}/${role}`);
  const hasGlobalRole = (await globalRoleRef.once('value')).val() === true;
  if (hasGlobalRole) return true;

  if (issuerCompanyId != null && issuerCompanyId !== '__all__') {
    const issuerCompanyRoleRef = firebase.database().ref(`roles/issuer_companies/${issuerCompanyId}/${userOrCompanySubpath}/${role}`);
    const hasIssuerCompanyRole = (await issuerCompanyRoleRef.once('value')).val() === true;
    if (hasIssuerCompanyRole) return true;
  }

  return false;
}

/**
 * Checks if a user has been granted a specific role by another company.
 * @param {string} uid The uid of the user who has received the role.
 * @param {string} role The role to check.
 * @param {string} [issuerCompanyId] The id of the company who has granted the role.
 * @param {object} [options]
 * @param {ClientOrAdminFirebase} [options.firebase]
 * @returns {Promise<Boolean>}
 */
export async function UserHasRole(uid, role, issuerCompanyId, options) {
  const { firebase } = options ?? {};
  return await HasRole({
    uid,
    role,
    issuerCompanyId,
    firebase
  });
}

/**
 * Checks if a company has been granted a specific role by another company.
 * @param {string} companyId The id of the company who has received the role.
 * @param {string} role The role to check.
 * @param {string} [issuerCompanyId] The id of the company who has granted the role.
 * @param {object} [options]
 * @param {ClientOrAdminFirebase} [options.firebase]
 * @returns {Promise<Boolean>}
 */
export async function CompanyHasRole(companyId, role, issuerCompanyId, options) {
  const { firebase } = options ?? {};
  return await HasRole({
    companyId,
    role,
    issuerCompanyId,
    firebase
  });
}

/**
 * Composes an object to update a specific role for a user or company.
 * @param {'user'|'company'} entityType - The type of entity whose role is being updated.
 * @param {string} entityId - The id of the entity whose role is being updated.
 * @param {string} role - The role to update.
 * @param {string} issuerCompanyId - The company granting the role.
 * @param {boolean} grantRole - The value to set the role to (`true` to grant, `null` to revoke).
 * @returns {Record<string, true|null>} The update object, based at `/roles`.
 */
export function ComposeRoleUpdate(entityType, entityId, role, issuerCompanyId, grantRole) {
  const userOrCompanySubpath = entityType === 'user' ? `users/${entityId}` : `companies/${entityId}`;
  const roleValue = grantRole === true ? true : null;
  return {
    [`issuer_companies/${issuerCompanyId}/${userOrCompanySubpath}/${role}`]: roleValue,
    [`recipient_lookup/${userOrCompanySubpath}/issuer_companies/${issuerCompanyId}/${role}`]: roleValue
  };
}

/**
 * Grants or revokes a specific role for a user or company by another company.
 * @param {object} options
 * @param {string} [options.uid] The uid of the user who is receiving the role.
 * @param {string} [options.companyId] The id of the company who is receiving the role.
 * @param {string} options.role The role to modify.
 * @param {string} options.issuerCompanyId The id of the company who is granting the role.
 * @param {Boolean} options.grantRole Whether to grant or revoke the role.
 * @param {ClientOrAdminFirebase} options.firebase
 * @returns {Promise<void>}
 */
async function SetRole(options) {
  const { uid, companyId, role, issuerCompanyId, grantRole, firebase } = options ?? {};

  EnsureUidOrCompanyIdExistButNotBoth(uid, companyId);
  EnsureRoleExists(role);
  EnsureIssuerCompanyIdExists(issuerCompanyId);
  EnsureFirebaseExists(firebase);

  if (grantRole !== true && grantRole !== false) throw new Error('grantRole must be a boolean');
  // Get the entity id (if uid is nullish, companyId will be non-nullish, thanks to EnsureUidOrCompanyIdExistButNotBoth)
  const entityId = uid ?? /** @type {string} */ (companyId);

  // Some user roles should be replicated in Firestore
  if (uid != null && ROLES_TO_REPLICATE_IN_FIRESTORE.includes(role)) {
    const userRolesDoc = firebase.firestore().doc(`users/${uid}/roles_by_issuer_company/${issuerCompanyId}`);
    // Add the role to the user's Firestore document
    if (grantRole === true) {
      await userRolesDoc.set({ [role]: true }, { merge: true });
    }
    // Remove the role from the user's Firestore document
    else {
      const userRoles = (await userRolesDoc.get()).data();
      if (!userRoles || userRoles[role] == null) return;
      delete userRoles[role];
      await userRolesDoc.set(userRoles);
    }
  }

  const update = ComposeRoleUpdate(uid ? 'user' : 'company', entityId, role, issuerCompanyId, grantRole);
  return await firebase.database().ref('roles').update(update);
}

/**
 * Grants or revokes a specific role for a user by another company. Should be called from server-side code.
 * @param {string} uid The uid of the user who is receiving the role.
 * @param {string} role The role to modify.
 * @param {string} issuerCompanyId The id of the company who is granting the role.
 * @param {Boolean} grantRole Whether to grant or revoke the role.
 * @param {object} options
 * @param {ClientOrAdminFirebase} options.firebase
 * @returns {Promise<void>}
 */
export async function SetUserRole(uid, role, issuerCompanyId, grantRole, options) {
  return await SetRole({
    uid,
    role,
    issuerCompanyId,
    grantRole,
    firebase: options.firebase
  });
}

/**
 * Requests the server grant or revoke a specific role for a user by another company.
 * @param {string} uid The uid of the user who is receiving the role.
 * @param {string} role The role to modify.
 * @param {string} issuerCompanyId The id of the company who is granting the role.
 * @param {Boolean} grantRole Whether to grant or revoke the role.
 * @returns {Promise<void>}
 */
export async function CallSetUserRole(uid, role, issuerCompanyId, grantRole) {
  const cloudFunctionOptions = {
    action: 'SET_USER_ROLE',
    uid,
    role,
    issuerCompanyId,
    grantRole
  };
  await clientFirebase.functions().httpsCallable('roles')(cloudFunctionOptions);
}

/**
 * Grants or revokes a specific role for a company by another company. Should be called from server-side code.
 * @param {string} companyId The id of the company who is receiving the role.
 * @param {string} role The role to modify.
 * @param {string} issuerCompanyId The id of the company who is granting the role.
 * @param {Boolean} grantRole Whether to grant or revoke the role.
 * @param {object} options
 * @param {ClientOrAdminFirebase} options.firebase
 * @returns {Promise<void>}
 */
export async function SetCompanyRole(companyId, role, issuerCompanyId, grantRole, options) {
  return await SetRole({
    companyId,
    role,
    issuerCompanyId,
    grantRole,
    firebase: options.firebase
  });
}

/**
 * @callback RoleChangeCallback
 * @param {Boolean} hasRole - Whether the user or company has the role.
 * @returns {void}
 */

/**
 * Observes changes to a specific role granted to a user or company by another company.
 * @param {object} options
 * @param {string} [options.uid] The uid of the user who has received the role.
 * @param {string} [options.companyId] The id of the company who has received the role.
 * @param {string} options.role The role to observe.
 * @param {String|null} options.issuerCompanyId The id of the company who has granted the role.
 * @param {RoleChangeCallback} options.callback The function to call when the role changes.
 * @param {ClientOrAdminFirebase} [options.firebase]
 * @returns {Function} - A method to detach the listener.
 */
function ListenToRole(options) {
  const { uid, companyId, role, issuerCompanyId, callback, firebase = clientFirebase } = options ?? {};

  EnsureUidOrCompanyIdExistButNotBoth(uid, companyId);
  EnsureRoleExists(role);
  EnsureFirebaseExists(firebase);
  EnsureCallbackExistsAndIsAFunction(callback);

  const userOrCompanySubpath = uid ? `users/${uid}` : `companies/${companyId}`;

  let hasGlobalRole = false;
  let hasIssuerCompanyRole = false;
  let hasRole = false;
  const roleChanged = (s) => {
    const newHasRole = hasGlobalRole || hasIssuerCompanyRole;
    if (newHasRole === hasRole) return;
    hasRole = newHasRole;
    callback(hasRole);
  };

  const globalRoleRef = firebase.database().ref(`roles/issuer_companies/__all__/${userOrCompanySubpath}/${role}`);
  const globalRoleListener = globalRoleRef.on('value', (s) => {
    hasGlobalRole = s.val() === true;
    roleChanged();
  });
  const disconnectGlobalRoleListener = () => globalRoleRef.off('value', globalRoleListener);

  let disconnectIssuerCompanyRoleListener = () => {};
  if (issuerCompanyId != null && issuerCompanyId !== '__all__') {
    const issuerCompanyRoleRef = firebase.database().ref(`roles/issuer_companies/${issuerCompanyId}/${userOrCompanySubpath}/${role}`);
    const issuerCompanyRoleListener = issuerCompanyRoleRef.on('value', (s) => {
      hasIssuerCompanyRole = s.val() === true;
      roleChanged();
    });
    disconnectIssuerCompanyRoleListener = () => issuerCompanyRoleRef.off('value', issuerCompanyRoleListener);
  }

  const disconnect = () => {
    disconnectGlobalRoleListener();
    disconnectIssuerCompanyRoleListener();
  };
  return disconnect;
}

/**
 * Observes changes to a specific role granted to a user by another company.
 * @param {string} uid The uid of the user who has received the role.
 * @param {string} role The role to observe.
 * @param {String|null} issuerCompanyId The id of the company who has granted the role.
 * @param {RoleChangeCallback} callback The function to call when the role changes.
 * @param {object} [options]
 * @param {ClientOrAdminFirebase} [options.firebase]
 * @returns {Function} - A method to detach the listener.
 */
export function ListenToUserRole(uid, role, issuerCompanyId, callback, options) {
  const { firebase } = options ?? {};
  return ListenToRole({
    uid,
    role,
    issuerCompanyId,
    callback,
    firebase
  });
}

/**
 * Observes changes to a specific role granted to a company by another company.
 * @param {string} companyId The id of the company who has received the role.
 * @param {string} role The role to observe.
 * @param {String|null} issuerCompanyId The id of the company who has granted the role.
 * @param {RoleChangeCallback} callback The function to call when the role changes.
 * @param {object} [options]
 * @param {ClientOrAdminFirebase} [options.firebase]
 * @returns {Function} - A method to detach the listener.
 */
export function ListenToCompanyRole(companyId, role, issuerCompanyId, callback, options) {
  const { firebase } = options ?? {};
  return ListenToRole({
    companyId,
    role,
    issuerCompanyId,
    callback,
    firebase
  });
}

/**
 * @callback RolesChangeCallback
 * @param {Record<string, Record<string, boolean>>} roles - A mapping of roles granted to the user or company by issuer companies.
 * @returns {void}
 */

/**
 * Observes changes to all roles granted to a user or company.
 * @param {object} options
 * @param {object} [options.issuer] The issuer config.
 * @param {string} [options.issuer.companyId] The id of the company who has granted the roles.
 * @param {object} [options.recipient] The recipient config.
 * @param {string} [options.recipient.uid] The uid of the user who has received the roles.
 * @param {string} [options.recipient.companyId] The id of the company who has received the roles.
 * @param {RolesChangeCallback} options.callback The function to call when there are role changes.
 * @param {ClientOrAdminFirebase} [options.firebase]
 * @returns {Function} - A method to detach the listener.
 */
function ListenToRoles(options) {
  const { issuer, recipient, callback, firebase = clientFirebase } = options ?? {};
  const { companyId: issuerCompanyId } = issuer ?? {};
  const { uid: recipientUid, companyId: recipientCompanyId } = recipient ?? {};

  EnsureUidOrCompanyIdExistButNotBoth(recipientUid, recipientCompanyId);
  EnsureFirebaseExists(firebase);
  EnsureCallbackExistsAndIsAFunction(callback);

  const userOrCompanySubpath = recipientUid ? `users/${recipientUid}` : `companies/${recipientCompanyId}`;
  const issuerCompaniesRef = firebase.database().ref(`roles/recipient_lookup/${userOrCompanySubpath}/issuer_companies`);
  const ref = issuerCompanyId != null ? issuerCompaniesRef.child(issuerCompanyId) : issuerCompaniesRef;

  const listener = ref.on('value', (s) => {
    const roles = s.val() ?? {};
    callback(roles);
  });

  const disconnect = () => ref.off('value', listener);
  return disconnect;
}

/**
 * Observes changes to all roles granted to a user.
 * @param {string} uid The uid of the user who has received the roles.
 * @param {RolesChangeCallback} callback The function to call when there are role changes.
 * @param {object} [options]
 * @param {ClientOrAdminFirebase} [options.firebase]
 * @returns {Function} - A method to detach the listener.
 */
export function ListenToUserRoles(uid, callback, options) {
  const { firebase } = options ?? {};
  return ListenToRoles({
    recipient: { uid },
    callback,
    firebase
  });
}

/**
 * Observes changes to all roles granted to a company.
 * @param {string} companyId The id of the company who has received the roles.
 * @param {RolesChangeCallback} callback The function to call when there are role changes.
 * @param {object} [options]
 * @param {ClientOrAdminFirebase} [options.firebase]
 * @returns {Function} - A method to detach the listener.
 */
export function ListenToCompanyRoles(companyId, callback, options) {
  const { firebase } = options ?? {};
  return ListenToRoles({
    recipient: { companyId },
    callback,
    firebase
  });
}

/**
 * Observes changes to all roles granted to a user by a specific company.
 * @param {string} uid The uid of the user who has received the roles.
 * @param {string} companyId The id of the company who has granted the roles.
 * @param {RolesChangeCallback} callback The function to call when there are role changes.
 * @param {object} [options]
 * @param {ClientOrAdminFirebase} [options.firebase]
 * @returns {Function} - A method to detach the listener.
 */
export function ListenToRolesGrantedToUserByCompany(uid, companyId, callback, options) {
  const { firebase } = options ?? {};
  return ListenToRoles({
    issuer: { companyId },
    recipient: { uid },
    callback,
    firebase
  });
}
